<script>
import { toMoney, toDecPercent, formatNumber } from '@utils/Format'
import { customSort as baseCustomSort } from '@utils/Sort'
import { decimalLengthQuantity } from '@utils/Asset'
import { FORMAT_ORIGEM } from '@constants/Text'
import EventIcon from '@modules/consolidator/components/event-icon/event-icon.vue'
import TableRowActions from '@modules/consolidator/components/table-row-actions/table-row-actions.vue'
import { CONSOLIDATOR_TABLE_CUSTOM_SORT_FIELDS } from '@modules/consolidator/constants/Text'
import {
  normalizeConsolidatorTableHeaders,
  buildEventRowActions,
} from '@modules/consolidator/utils/table/table'
import {
  hasZeroedValues,
  isBonusEvent,
  relabelUnconvertedValue,
  resolveEventDisplayText,
  resolveEventRowCurrency,
  resolveEventAssetCurrency,
} from '@modules/consolidator/utils/Events/Events'

export default {
  name: 'EventosTable',
  components: {
    EventIcon,
    TableRowActions,
  },
  props: {
    asset: {
      type: Object,
      default: () => ({}),
    },
    search: {
      type: String,
      default: '',
    },
    sortBy: {
      type: Array,
      default: () => ['data_pagamento'],
    },
    sortDesc: {
      type: Array,
      default: () => [true],
    },
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showAssetNameColumn: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      formatNumber,
      FORMAT_ORIGEM,
    }
  },
  computed: {
    eventos() {
      const list = Array.isArray(this.items) ? this.items : []
      // Resolve moeda e texto POR LINHA aqui, nao no template: o `CLAUDE.md` do
      // repo proibe chamada de funcao em interpolacao (cada re-render reexecuta).
      return list
        .filter((eventItem) => !hasZeroedValues(eventItem))
        .map((eventItem) => {
          const moeda = this.rowCurrency(eventItem)
          const ehBonificacao = isBonusEvent(eventItem)
          return {
            ...eventItem,
            _valorText: ehBonificacao
              ? toDecPercent(eventItem.valor)
              : this.rowValueText(eventItem, moeda),
            _valorTotalText: this.rowTotalText(eventItem, moeda),
          }
        })
    },
    headers() {
      const baseHeaders = []
      const hasAsset = !!(this.asset && this.asset.id)
      const shouldIncludeAssetColumn = !hasAsset || this.showAssetNameColumn

      if (shouldIncludeAssetColumn) {
        baseHeaders.push({ text: 'Ativo', value: 'nome_ativo' })
      }

      baseHeaders.push(
        { text: 'Tipo', value: 'operacao' },
        { text: 'Data competência', value: 'data_com' },
        { text: 'Data pagamento', value: 'data_pagamento' },
        { text: 'Quantidade', value: 'quantidade' },
        { text: 'Valor unitário', value: 'valor' },
        { text: 'Valor Total', value: 'valor_total' },
        { text: 'Origem', value: 'origem' }
      )

      return normalizeConsolidatorTableHeaders({
        headers: baseHeaders,
        includeActions: !this.readOnly,
      })
    },
  },
  methods: {
    /**
     * Moeda de UMA linha. As duas colunas de dinheiro desta tabela mudam de
     * moeda conforme a linha, e o discriminador e a CONVERSAO dentro do texto —
     * nao o tipo do campo:
     *
     *   "R$ 1,07 (US$ 0,20 x R$ 5,37)"  converteu -> o `valor_total` que
     *                                   acompanha esta em REAIS
     *   "R$ 0,20 (- x -)"               nao converteu -> o numero e o
     *                                   `valor_total` estao na moeda do ATIVO
     *   0.2 (numero)                    e o que ACABAMOS de gravar, na moeda
     *                                   do ativo
     *
     * Medido nos dois estados do MESMO ativo (186922 / cliente 25925): antes,
     * 2152,53 / 2005,491 = 1,07 (reais); depois, 401,10 / 2005,491 = 0,20
     * (dolares). Testar "e texto?" classificava o segundo como reais e trocava
     * o erro de lado.
     */
    rowCurrency(item) {
      return resolveEventRowCurrency({
        value: item && item.valor,
        currency: this.assetCurrency(item),
      })
    },
    /**
     * A moeda da LINHA vence a do ativo. Nas telas multi-ativo (Movimentacoes,
     * extrato) a tabela monta sem `:asset`, entao ler so `this.asset.moeda`
     * devolvia `''` -> reais em qualquer linha, e um provento em dolar aparecia
     * rotulado como real. Mesma regra do `movements-events-combined-table`, que
     * ja tem o lock disso — os dois componentes discordavam do mesmo conceito.
     */
    assetCurrency(item) {
      return resolveEventAssetCurrency({ item, asset: this.asset })
    },
    /**
     * Texto da coluna "Valor". Alem da moeda do `toMoney`, corrige o rotulo do
     * proprio texto quando a cotizacao nao converteu — ver
     * `relabelUnconvertedValue`.
     */
    rowValueText(item, moeda) {
      const textoDoBackend = resolveEventDisplayText({
        event: item,
        currency: this.assetCurrency(item),
      })
      if (textoDoBackend) {
        return textoDoBackend
      }
      const valor = relabelUnconvertedValue({
        value: item && item.valor,
        currency: this.assetCurrency(item),
      })
      return toMoney(valor, 2, moeda)
    },
    rowTotalText(item, moeda) {
      const textoDoBackend = resolveEventDisplayText({
        event: item,
        campo: 'valor_total',
        currency: this.assetCurrency(item),
      })
      if (textoDoBackend) {
        return textoDoBackend
      }
      return toMoney(item && item.valor_total, 2, moeda)
    },
    quantityText({ item }) {
      if (item && item.quantidade_f) {
        return item.quantidade_f
      }

      const hasQuantity = item && (item.quantidade || item.quantidade === 0)
      if (!hasQuantity) {
        return '-'
      }

      const decimalTarget =
        this.asset && this.asset.tipo ? this.asset : item || {}

      return this.formatNumber(item.quantidade, decimalLengthQuantity(decimalTarget))
    },
    customSort(items, index, isDec) {
      return baseCustomSort(
        items,
        index,
        isDec,
        CONSOLIDATOR_TABLE_CUSTOM_SORT_FIELDS
      )
    },
    onEditEvent(item) {
      this.$emit('edit-event', item)
    },
    onRemoveEvent(item) {
      this.$emit('remove-event', item)
    },
    getEventActions({ item }) {
      return buildEventRowActions({
        item,
        onEdit: this.onEditEvent,
        onRemove: this.onRemoveEvent,
      })
    },
  },
}
</script>

<template>
  <div class="eventos-table">
    <BaseTable
      :title="title"
      :custom-sort="customSort"
      :headers="headers"
      :items="eventos"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :loading="loading"
      :search-value="search"
      disable-margin-top
    >
      <template v-slot:cornerAction>
        <slot name="cornerAction" />
      </template>

      <template v-slot:item.icon="{ item }">
        <EventIcon :event="item" />
      </template>

      <template v-slot:item.nome_ativo="{ item }">
        {{ item.nome_ativo || (asset && asset.nome_ativo) || '' }}
      </template>

      <template v-slot:item.operacao="{ item }">
        {{ item.operacao || '' }}
        <template
          v-if="
            item.operacao &&
            ['Desdobramento', 'Agrupamento'].includes(item.operacao)
          "
        >
          <span> de {{ item.de }}:{{ item.para }}</span>
        </template>
      </template>

      <template v-slot:item.data_com="{ item }">
        {{ item.data_com || '' }}
      </template>

      <template v-slot:item.data_pagamento="{ item }">
        {{ item.data_pagamento || '' }}
      </template>

      <template v-slot:item.quantidade="{ item }">
        {{ quantityText({ item }) }}
      </template>

      <template v-slot:item.valor="{ item }">
        {{ item._valorText }}
      </template>

      <template v-slot:item.valor_total="{ item }">
        <template v-if="['Bonificação (%)'].includes(item.operacao)">
          {{ item.valor_total }}x
          {{ item.nome_ativo || (asset && asset.referencia) }}
        </template>
        <template v-else>
          {{ item._valorTotalText }}
        </template>
      </template>

      <template v-slot:item.origem="{ item }">
        {{ FORMAT_ORIGEM[item.origem] || item.origem || '-' }}
      </template>

      <template v-slot:item.action="{ item }">
        <TableRowActions :actions="getEventActions({ item })" />
      </template>
    </BaseTable>
  </div>
</template>

<style lang="sass">
.eventos-table
  width: 100%
</style>
